/**
 * `useHeartbeat` — sliding-window SPA session keep-alive.
 *
 * Mounted once per authenticated app shell (Storefront `CustomerLayout`,
 * Console `AdminLayout`). While `state.auth.isAuthenticated === true` and
 * the tab is visible, it fires `POST /auth/heartbeat` every 60 seconds via
 * the shared auth client so the server can rotate the access token under
 * the sliding-window model described in spec §5.3.8.
 *
 * Behavior
 * --------
 * - Reads `state.auth.isAuthenticated` via `useAppSelector`; no-ops when
 *   unauthenticated.
 * - While visible + authenticated, `setInterval` ticks every `intervalMs`
 *   (default 60s). Each tick calls `heartbeat()` from the shared client —
 *   the client's request / response interceptors handle the Authorization
 *   header and any silent refresh.
 * - On success: the shared client writes the rotated tokens into
 *   `tokenStore` itself; this hook only dispatches
 *   `authSlice.actions.heartbeatSuccess(payload)` so the Redux mirror of
 *   `expiresAt` / `isAuthenticated` stays in sync.
 * - On 401: dispatches `authSlice.actions.logout()` and tears down the
 *   interval — the user is signed out and there is nothing to keep alive.
 * - On any other error (network blip, 5xx, CORS, etc.): swallows the
 *   failure and keeps ticking so a transient outage does not evict the
 *   user.
 * - Pauses on `visibilitychange` → `hidden`; resumes on `→ visible`.
 * - Cleans up the interval + `visibilitychange` listener on unmount,
 *   logout (`isAuthenticated` flips to false), and `enabled: false`.
 *
 * SH-AUTH-003.
 */
import { useEffect } from 'react';

import { heartbeat as heartbeatRequest, AuthApiError } from '@dfq/shared/api/auth';
import { useAppDispatch, useAppSelector } from '@dfq/shared/stores/hooks';
import { authSlice, logout } from '@dfq/shared/stores/authSlice';

/** Default cadence per spec §5.3.8 — one heartbeat per minute. */
const DEFAULT_INTERVAL_MS = 60_000;

export interface UseHeartbeatOptions {
  /** Override the 60 000 ms cadence. Primarily a test-harness knob. */
  intervalMs?: number;
  /**
   * Master switch. When `false`, the hook unmounts its interval without
   * unmounting the component — useful for feature flags and for the
   * authenticated-but-inside-MFA-challenge screens where keep-alive must
   * not fire yet.
   */
  enabled?: boolean;
}

/**
 * Narrow an unknown error to "looks like HTTP 401 from the heartbeat
 * endpoint". We accept either an axios error with `response.status === 401`
 * (the common shape) or an `AuthApiError` whose `code` was mapped from the
 * 401 envelope. Anything else is treated as a transient failure.
 */
function isUnauthorized(error: unknown): boolean {
  if (error === null || typeof error !== 'object') {
    return false;
  }
  const maybeAxios = error as {
    response?: { status?: number };
    status?: number;
  };
  if (maybeAxios.response?.status === 401 || maybeAxios.status === 401) {
    return true;
  }
  if (error instanceof AuthApiError) {
    // The client sets `error_code` on unauthenticated envelopes via
    // `Handler::unauthorized()`. Treat the canonical codes as 401s.
    return (
      error.code === 'UNAUTHENTICATED' ||
      error.code === 'TOKEN_EXPIRED' ||
      error.code === 'SESSION_EXPIRED'
    );
  }
  return false;
}

export function useHeartbeat(options?: UseHeartbeatOptions): void {
  const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;
  const enabled = options?.enabled ?? true;
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const tick = async (): Promise<void> => {
      // Guard against the tab having been backgrounded between ticks.
      if (
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      try {
        const result = await heartbeatRequest();
        if (cancelled) return;
        dispatch(authSlice.actions.heartbeatSuccess(result.authorization));
      } catch (error) {
        if (cancelled) return;
        if (isUnauthorized(error)) {
          // Stop the interval FIRST so the logout dispatch does not race
          // another tick that is already queued.
          if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
          }
          // Dispatch the `logout` thunk so the server-side session is
          // revoked and local state (slice + token store + persisted
          // user) is wiped by the existing `logout.fulfilled` reducer.
          void dispatch(logout());
          return;
        }
        // Transient error — leave the interval running; next tick retries.
      }
    };

    const start = (): void => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        void tick();
      }, intervalMs);
    };

    const stop = (): void => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = (): void => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
      }
    };

    // Only start immediately if the tab is currently visible. If the app
    // mounts into a hidden tab (browser restore into background), wait for
    // the first `visibilitychange → visible` before ticking.
    if (
      typeof document === 'undefined' ||
      document.visibilityState === 'visible'
    ) {
      start();
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [enabled, intervalMs, isAuthenticated, dispatch]);
}

/** Exposed for unit tests that want to assert against the default. */
export const USE_HEARTBEAT_DEFAULT_INTERVAL_MS = DEFAULT_INTERVAL_MS;
